import React from 'react';
import { LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Box, Tabs, Tab } from '@mui/material';
import { GRAPH_TYPE_DAILY } from '../../utils/enums';
import { useLazyGetUserGraphQuery } from '../../services/dashboard';

type TimeRange = GRAPH_TYPE_DAILY;
type RoleType = 'USER' | 'OWNER';

const UserGraph = () => {
  const [selectedRole, setSelectedRole] = React.useState<RoleType>('USER');
  const [selectedRange, setSelectedRange] = React.useState<TimeRange>(GRAPH_TYPE_DAILY.DAILY);
  const [graphData, setGraphData] = React.useState<Array<{ name: string; Users: number }>>([]);
  const [triggerUserGraph, { isFetching }] = useLazyGetUserGraphQuery();

  const fetchData = React.useCallback(async () => {
    try {
      const res = await triggerUserGraph({
        from: 1,
        size: 10,
        search: '',
        role: selectedRole,
        type: selectedRange,
      }).unwrap();
      if (res?.statusCode === 200 && Array.isArray(res.data)) {
        const transformed = res.data.map((item: any) => ({ name: item.name, Users: Number(item.count || 0) }));
        setGraphData(transformed);
      } else {
        setGraphData([]);
      }
    } catch (_e) {
      setGraphData([]);
    }
  }, [selectedRole, selectedRange, triggerUserGraph]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const roleTabs: Array<{ key: RoleType; label: string }> = [
    { key: 'USER', label: 'User' },
    { key: 'OWNER', label: 'Owner' },
  ];

  const rangeTabs: Array<{ key: TimeRange; label: string }> = [
    { key: GRAPH_TYPE_DAILY.DAILY, label: 'A day' },
    { key: GRAPH_TYPE_DAILY.WEEKLY, label: 'A week' },
    { key: GRAPH_TYPE_DAILY.MONTHLY, label: 'A month' },
    { key: GRAPH_TYPE_DAILY.YEARLY, label: 'A year' },
  ];

  return (
    <div className="cards table_card">
      <div className="cards_header">
        <div className="left_s">
          <h2>Total Users</h2>
        </div>
        <div className="right_s">
          <Box className="custom_tabs1" sx={{ width: '100%' }}>
            <Tabs
              value={roleTabs.findIndex(rt => rt.key === selectedRole)}
              onChange={(_e, idx) => setSelectedRole(roleTabs[idx].key)}
              aria-label="role tabs"
              className="custom_tabs_links"
            >
              {roleTabs.map((tab, idx) => (
                <Tab key={tab.key} label={tab.label} id={`role-tab-${idx}`} aria-controls={`role-tabpanel-${idx}`} />
              ))}
            </Tabs>
          </Box>
          <p>
            {rangeTabs.map((tab) => (
              <span
                key={tab.key}
                onClick={() => setSelectedRange(tab.key)}
                style={{ cursor: 'pointer', fontWeight: selectedRange === tab.key ? 700 : 400 }}
              >
                {tab.label}
              </span>
            ))}
          </p>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={350}>
        <LineChart data={graphData} margin={{ top: 20, right: 5, left: 5, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis allowDecimals={false} />
          <Tooltip />
          <Line type="monotone" dataKey="Users" stroke="#e71c57" strokeWidth={2} activeDot={{ r: 8 }} isAnimationActive={!isFetching} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default UserGraph;
